import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  ApiError,
  getUserSettings,
  listCreditorTickets,
  useAuthSession,
} from '@/api';
import type { TicketListItem } from '@/domain/tickets';
import {
  buildCreditorListRequest,
  canShowKreditBetBanner,
  kreditBetBlockedByRewards,
} from '@/domain/kredit-bet';
import type { PlaceBetMessage } from '@/domain/place-bet';
import { betslipSetCreditor, getBetslipTicket } from './betslip-store';
import { useBetslipTicket } from './useBetslip';

export type KreditBetView = 'idle' | 'selecting';

export interface UseKreditBetResult {
  /** Show the promo banner (eligible + has selections + not connected). */
  showBanner: boolean;
  /** Kreditor ticket is linked. */
  connected: boolean;
  view: KreditBetView;
  creditorTickets: TicketListItem[];
  isLoadingList: boolean;
  errorMessage: PlaceBetMessage | null;
  termsUrl: string | null;
  tryKreditBet: () => Promise<void>;
  selectCreditor: (ticket: TicketListItem) => void;
  dismissCreditor: () => void;
  exitSelecting: () => void;
  clearError: () => void;
}

/**
 * KreditBet betslip flow: Try it → creditor list → select → connected place path.
 */
export function useKreditBet(): UseKreditBetResult {
  const { t } = useTranslation();
  const { user, loggedIn } = useAuthSession();
  const ticket = useBetslipTicket();
  const [view, setView] = useState<KreditBetView>('idle');
  const [creditorTickets, setCreditorTickets] = useState<TicketListItem[]>([]);
  const [errorMessage, setErrorMessage] = useState<PlaceBetMessage | null>(null);

  const settings = user?.CreditBetSettings ?? getUserSettings()?.CreditBetSettings;
  const membership = user?.Membership ?? getUserSettings()?.Membership;
  const connected = Boolean(ticket.CreditorNumber);
  const showBanner =
    loggedIn &&
    !connected &&
    view === 'idle' &&
    canShowKreditBetBanner(ticket, settings, membership);

  const termsUrl =
    typeof settings?.PageUrl === 'string' && settings.PageUrl.trim()
      ? settings.PageUrl.trim()
      : null;

  const listMutation = useMutation({
    mutationFn: async (): Promise<TicketListItem[]> => {
      const current = getBetslipTicket();
      const userGuid = (user?.Guid ?? getUserSettings()?.Guid ?? '').trim();
      if (!userGuid) {
        throw new Error('Login');
      }
      // Must include User (machine GUID) — UsersAPI returns [] when machine lookup fails.
      return listCreditorTickets(buildCreditorListRequest(current, userGuid));
    },
  });

  const tryKreditBet = useCallback(async () => {
    setErrorMessage(null);
    const current = getBetslipTicket();
    const blocked = kreditBetBlockedByRewards(current);
    if (blocked) {
      setErrorMessage(blocked);
      return;
    }

    const userGuid = (user?.Guid ?? getUserSettings()?.Guid ?? '').trim();
    if (!userGuid) {
      setErrorMessage({
        key: 'Login',
        defaultValue: 'Please log in to place a bet',
      });
      return;
    }

    try {
      const tickets = await listMutation.mutateAsync();
      if (!tickets.length) {
        setErrorMessage({
          key: 'NoOpenTicketsForCreditBet',
          defaultValue: t('NoOpenTicketsForCreditBet', {
            defaultValue: 'No open tickets available for KreditBet',
          }),
        });
        setCreditorTickets([]);
        setView('idle');
        return;
      }
      setCreditorTickets(tickets);
      setView('selecting');
    } catch (error) {
      setErrorMessage({
        key: 'Error',
        defaultValue:
          error instanceof ApiError
            ? error.message
            : t('Error', { defaultValue: 'Unable to load KreditBet tickets' }),
      });
      setView('idle');
    }
  }, [listMutation, t, user?.Guid]);

  const selectCreditor = useCallback((selected: TicketListItem) => {
    betslipSetCreditor({
      CreditorNumber: selected.Id,
      CreditorType: typeof selected.Type === 'number' ? selected.Type : Number(selected.Type) || 0,
      CreditorPossibleWin: selected.PossibleWin,
    });
    setView('idle');
    setCreditorTickets([]);
    setErrorMessage(null);
  }, []);

  const dismissCreditor = useCallback(() => {
    betslipSetCreditor(null);
    setView('idle');
    setErrorMessage(null);
  }, []);

  const exitSelecting = useCallback(() => {
    setView('idle');
    setCreditorTickets([]);
  }, []);

  return {
    showBanner,
    connected,
    view,
    creditorTickets,
    isLoadingList: listMutation.isPending,
    errorMessage,
    termsUrl,
    tryKreditBet,
    selectCreditor,
    dismissCreditor,
    exitSelecting,
    clearError: () => setErrorMessage(null),
  };
}
